import React, { useState, useEffect } from 'react';
// Local Imports
import {
  DeleteIcon,
  SuspendUserIcon,
  ResendPasswordIcon,
  NotSuspendUserIcon,
  StripeIcon,
  CopiedIcon,
  AvailabilityIcon,
} from '../../assets/icons';
import {
  Alert,
  Button,
  Modal,
  Spinner,
  TextField,
} from '../../components/shared';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import {
  forgetPasswordAPI,
  suspendUserAPI,
  deleteUserAPI,
  unSuspendUserAPI,
  getStripeLink,
  checkUserAvailability,
  updateUserInfo,
} from '../../redux/features/auth/authSlice';
import EditUserIcon from '../../assets/icons/EditUserIcon';

type Time = {
  time: string;
  selected: boolean;
  value: number;
  isNotActive: boolean;
};

type SelectedDate = {
  date: string;
  selected: boolean;
};

type AvailableTime = {
  start: Time;
  end: Time;
};

type AvailabilityProps = {
  coordinates: { [key: string]: any };
  _id: string;
  availability: string;
  location: string;
  selectedDates: SelectedDate[];
  availableTime: AvailableTime[];
};

// Popover Children
export const Children = (
  restProps: { [key: string]: any },
  recallList: () => void
) => {
  //Stats
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.authUser);
  const [userData, setUserData] = useState({
    email: restProps?.row?.email,
    phoneNumber: restProps?.row?.phoneNumber,
    firstName: restProps?.row?.profile?.firstName,
    lastName: restProps?.row?.profile?.lastName,
    state: restProps?.row?.profile?.state,
    zipcode: restProps?.row?.profile?.zipcode,
    address: restProps?.row?.profile?.address,
  });
  const [isOpenEditUserModal, setIsOpenEditUserModal] = useState(false);
  const [isOpenResetModal, setIsOpenResetModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenSuspendModal, setIsOpenSuspendModal] = useState(false);
  const [isOpenStripeModal, setIsOpenStripeModal] = useState(false);
  const [isOpenAvailabilityModal, setIsOpenAvailabilityModal] = useState(false);
  const [availabilityData, setAvailabilityData] = useState<AvailabilityProps>();
  const [stripeLink, setStripeLink] = useState<string>('');
  const [isCopied, setIsCopied] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  // Reset Password Link API
  const handleResetPasswordLink = async () => {
    setIsError(false);
    setIsSuccess(false);
    const data = await dispatch(
      forgetPasswordAPI({ userId: restProps?.row?.id })
    );

    if (data.meta.requestStatus === 'rejected') {
      setIsError(true);
    }
    if (data.meta.requestStatus === 'fulfilled') {
      setIsSuccess(true);
      setSuccessMessage(data.payload?.message);
    }
  };

  // Suspend User API
  const handleSuspendUser = async () => {
    setIsError(false);
    setIsSuccess(false);
    let data;
    data = await dispatch(
      restProps?.row?.status
        ? unSuspendUserAPI({ userId: restProps?.row?.id })
        : suspendUserAPI({ userId: restProps?.row?.id })
    );
    if (data.meta.requestStatus === 'rejected') {
      setIsError(true);
    }
    if (data.meta.requestStatus === 'fulfilled') {
      setIsSuccess(true);
      setSuccessMessage(data.payload?.message);
    }
  };

  // Delete User API
  const handleDeleteUser = async () => {
    setIsError(false);
    setIsSuccess(false);
    const data = await dispatch(deleteUserAPI({ userId: restProps?.row?.id }));
    if (data.meta.requestStatus === 'rejected') {
      setIsError(true);
    }

    if (data.meta.requestStatus === 'fulfilled') {
      setIsSuccess(true);
      setSuccessMessage(data.payload?.message);
      recallList();
    }
  };

  // Delete User API
  const handleCreateStripe = async () => {
    setIsError(false);
    setIsSuccess(false);
    const data = await dispatch(getStripeLink({ userId: restProps?.row?.id }));
    if (data.meta.requestStatus === 'rejected') {
      setIsError(true);
    }
    if (data.meta.requestStatus === 'fulfilled') {
      setStripeLink(data?.payload?.data?.url);
      setIsSuccess(true);
      setSuccessMessage(data.payload?.message);
    }
  };

  // Handle Check User Availability
  const handleCheckAvailability = async () => {
    setIsError(false);
    setIsSuccess(false);
    const data = await dispatch(
      checkUserAvailability({ userId: restProps?.row?.id })
    );
    if (data.meta.requestStatus === 'rejected') {
      setIsError(true);
    }
    if (data.meta.requestStatus === 'fulfilled') {
      setAvailabilityData(data?.payload?.data);
      setIsSuccess(true);
      setSuccessMessage(data.payload?.message);
    }
  };

  // Handle Close
  const onClose = () => {
    setIsOpenResetModal(false);
    setIsOpenDeleteModal(false);
    setIsOpenSuspendModal(false);
    setIsOpenStripeModal(false);
    setSuccessMessage('');
    setIsError(false);
    setIsSuccess(false);
    recallList();
  };

  //Hanlde Copied
  const handleCopied = () => {
    if (window) {
      window.navigator.clipboard.writeText(stripeLink).then((res) => {
        setIsCopied(true);
      });
    }
  };

  //Handle Open URL
  const handleOpenLin = () => {
    if (stripeLink) {
      window.open(stripeLink, '_blank');
      onClose();
    }
  };

  //Hanlde OnChange
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Update User
  const handleUpdateUser = async () => {
    const payload = {
      id: restProps?.row?.profile?._id,
      firstName: userData.firstName,
      lastName: userData.lastName,
      state: userData.state,
      zipcode: userData.zipcode,
      address: userData.address,
    };

    setIsError(false);
    setIsSuccess(false);
    const data = await dispatch(updateUserInfo(payload));
    if (data.meta.requestStatus === 'rejected') {
      setIsError(true);
    }

    if (data.meta.requestStatus === 'fulfilled') {
      setIsSuccess(true);
      setSuccessMessage(data.payload?.message);
      recallList();
    }
  };

  // UseEffect For Render Cycle
  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 1000);
    }
  }, [isCopied]);

  return (
    <React.Fragment>
      <div className='flex flex-col'>
        {/* Reset  Password Link */}
        <button
          className='hover:cursor-pointer  transition-all duration-300 rounded-[4px] hover:bg-gray-200 p-2 flex justify-start items-start gap-2'
          onClick={() => setIsOpenEditUserModal(true)}
        >
          <EditUserIcon className='w-[20px] h-[20px]' />
          Edit User
        </button>

        {/* Reset  Password Link */}
        <button
          className='hover:cursor-pointer  transition-all duration-300 rounded-[4px] hover:bg-gray-200 p-2 flex justify-start items-start gap-2'
          onClick={() => setIsOpenResetModal(true)}
        >
          <ResendPasswordIcon className='w-[20px] h-[20px]' />
          Reset Password Link
        </button>

        {/* Suspend User */}
        {restProps?.row?.status ? (
          <button
            className='hover:cursor-pointer transition-all duration-300 rounded-[4px] hover:bg-gray-200 p-2 flex items-center gap-2'
            onClick={() => setIsOpenSuspendModal(true)}
          >
            <NotSuspendUserIcon className='w-[20px] h-[20px]' />
            Restore User
          </button>
        ) : (
          <button
            className='hover:cursor-pointer transition-all duration-300 rounded-[4px] hover:bg-gray-200 p-2 flex items-center gap-2'
            onClick={() => setIsOpenSuspendModal(true)}
          >
            <SuspendUserIcon className='w-[20px] h-[20px]' />
            Suspend User
          </button>
        )}

        {/* Availability Link */}
        <button
          className='hover:cursor-pointer transition-all duration-300 rounded-[4px] hover:bg-gray-200 p-2 flex items-center gap-2'
          onClick={() => {
            setIsOpenAvailabilityModal(true);
            handleCheckAvailability();
          }}
        >
          <AvailabilityIcon className='w-[20px] h-[20px]' />
          Availability
        </button>

        {/* Stripe Link */}
        <button
          className='hover:cursor-pointer transition-all duration-300 rounded-[4px] hover:bg-gray-200 p-2 flex items-center gap-2'
          onClick={() => {
            setIsOpenStripeModal(true);
            handleCreateStripe();
          }}
        >
          <StripeIcon className='w-[20px] h-[20px]' />
          Stripe Link
        </button>

        {/* Delete User */}
        <button
          className='hover:cursor-pointer transition-all duration-300 rounded-[4px] hover:bg-gray-200 p-2 flex items-center gap-2'
          onClick={() => setIsOpenDeleteModal(true)}
        >
          <DeleteIcon />
          Delete User
        </button>
      </div>

      {/* Resend Password Link Modal */}
      <Modal
        open={isOpenResetModal}
        onClose={() => {
          setIsOpenResetModal(false);
          onClose();
        }}
        title='Reset Password'
        icon={<ResendPasswordIcon className='w-[20px] h-[20px]' />}
      >
        <div className='mt-4'>
          {!isSuccess && (
            <p className='text-[1rem] leading-relaxed'>
              Are you sure you want to send a password reset link to this user?
            </p>
          )}
          <Alert
            color={isError ? 'danger' : isSuccess ? 'success' : 'danger'}
            text={
              isError
                ? error
                  ? error
                  : 'Something went wrong'
                : isSuccess
                ? successMessage
                : null
            }
            showAlert={isError || isSuccess}
            onClose={onClose}
            isShowClose={isSuccess ? false : true}
          />
        </div>
        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='secondary' color='' onClick={onClose}>
            {isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!isSuccess ? (
            <Button
              variant='primary'
              color='primary'
              onClick={handleResetPasswordLink}
              loading={loading}
            >
              Confirm
            </Button>
          ) : null}
        </div>
      </Modal>

      {/* Suspend User Modal */}
      <Modal
        open={isOpenSuspendModal}
        onClose={() => {
          setIsOpenSuspendModal(false);
          onClose();
        }}
        title={restProps?.row?.status ? 'Restore User' : 'Suspend User'}
        icon={
          restProps?.row?.status ? (
            <NotSuspendUserIcon className='w-[20px] h-[20px]' />
          ) : (
            <SuspendUserIcon className='w-[20px] h-[20px]' />
          )
        }
      >
        <div className='mt-4'>
          {!isSuccess && (
            <p className='text-[1rem] leading-relaxed'>
              {restProps?.row?.status
                ? 'Are you sure you want to restore this user?'
                : 'Are you sure you want to suspend this user?'}
            </p>
          )}
          <Alert
            color={isError ? 'danger' : isSuccess ? 'success' : 'danger'}
            text={
              isError
                ? error
                  ? error
                  : 'Something went wrong'
                : isSuccess
                ? successMessage
                : null
            }
            showAlert={isError || isSuccess}
            onClose={onClose}
            isShowClose={isSuccess ? false : true}
          />
        </div>
        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='secondary' color='' onClick={onClose}>
            {isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!isSuccess ? (
            <Button
              variant='primary'
              color='primary'
              onClick={handleSuspendUser}
              loading={loading}
            >
              Confirm
            </Button>
          ) : null}
        </div>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        open={isOpenDeleteModal}
        onClose={() => {
          setIsOpenDeleteModal(false);
          onClose();
        }}
        title='Delete User'
        icon={<DeleteIcon className='w-[20px] h-[20px]' />}
      >
        <div className='mt-4'>
          {!isSuccess && (
            <p className='text-[1rem] leading-relaxed'>
              Are you sure you want to delete this user?
            </p>
          )}
          <Alert
            color={isError ? 'danger' : isSuccess ? 'success' : 'danger'}
            text={
              isError
                ? error
                  ? error
                  : 'Something went wrong'
                : isSuccess
                ? successMessage
                : null
            }
            showAlert={isError || isSuccess}
            onClose={onClose}
            isShowClose={isSuccess ? false : true}
          />
        </div>
        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='secondary' color='' onClick={onClose}>
            {isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!isSuccess ? (
            <Button
              variant='primary'
              color='danger'
              onClick={handleDeleteUser}
              loading={loading}
            >
              Confirm
            </Button>
          ) : null}
        </div>
      </Modal>

      {/* Stripe Account Modal */}
      <Modal
        open={isOpenStripeModal}
        onClose={() => {
          setIsOpenStripeModal(false);
          onClose();
        }}
        title='Stripe Account Link'
        icon={<StripeIcon className='w-[20px] h-[20px]' />}
      >
        <div className='mt-4 '>
          {loading ? (
            <Spinner width='w-8' height='h-8' border='border-[4px]' />
          ) : (
            isSuccess && (
              <div className='flex  items-center gap-2'>
                <p
                  className='text-[1rem] leading-relaxed max-w-96 truncate bg-gray-200 rounded hover:text-[#3362DA] p-1 cursor-pointer'
                  onClick={handleOpenLin}
                >
                  {stripeLink}
                </p>
                {isCopied && (
                  <span className='absolute left-[420px] top-[50px] p-[2px] rounded'>
                    Copied
                  </span>
                )}
                <CopiedIcon
                  className='w-[20px] h-[20px] cursor-pointer'
                  onClick={handleCopied}
                />
              </div>
            )
          )}
          <Alert
            color={isError ? 'danger' : isSuccess ? 'success' : 'danger'}
            text={
              isError
                ? error
                  ? error
                  : 'Something went wrong'
                : isSuccess
                ? successMessage
                : null
            }
            showAlert={isError || isSuccess}
            onClose={onClose}
            isShowClose={isSuccess ? false : true}
          />
        </div>
        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='secondary' color='' onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>

      {/* Availability Modal */}
      <Modal
        open={isOpenAvailabilityModal}
        onClose={() => {
          setIsOpenAvailabilityModal(false);
          onClose();
        }}
        title='User Availability'
        icon={<AvailabilityIcon className='w-[20px] h-[20px]' />}
      >
        <div className='mt-4 '>
          {loading ? (
            <Spinner width='w-8' height='h-8' border='border-[4px]' />
          ) : (
            isSuccess && (
              <div className='w-full '>
                {/* Schedule Details */}
                <div className='flex w-full justify-between'>
                  <p className='w-full text-[1rem] leading-relaxed'>
                    Schedule Type
                  </p>
                  {availabilityData && (
                    <p className='w-full text-[1rem] leading-relaxed bg-slate-100 px-2 rounded capitalize'>
                      {availabilityData.availability}
                    </p>
                  )}
                </div>
                <div className='flex w-full justify-between mt-2'>
                  <p className='w-full text-[1rem] leading-relaxed'>Location</p>
                  {availabilityData && (
                    <p className='w-full text-[1rem] leading-relaxed bg-slate-100 px-2 rounded'>
                      {availabilityData.location || 'N/A'}
                    </p>
                  )}
                </div>
                {/* Availability Time */}
                {availabilityData?.availableTime &&
                availabilityData?.availableTime?.length > 0 ? (
                  <div className='w-full mt-2'>
                    <p className='w-full text-[1rem] leading-relaxed'>
                      Available Time
                    </p>
                    <table className='min-w-full bg-white text-sm'>
                      <thead className='sticky top-0 border-b bg-white ltr:text-left rtl:text-right'>
                        <tr>
                          <th className='whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900'>
                            Start Time
                          </th>
                          <th className='whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900'>
                            End Time
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-200'>
                        {availabilityData?.availableTime.map((time, index) => {
                          return (
                            <tr className='odd:bg-gray-50' key={index}>
                              <td className='whitespace-nowrap px-4 py-2 font-medium text-gray-900'>
                                {time.start.time || '-'}
                              </td>
                              <td className='whitespace-nowrap px-4 py-2 text-gray-700'>
                                {time.end.time || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                {/* Availability Dates */}
                {availabilityData?.selectedDates &&
                availabilityData?.selectedDates?.length > 0 ? (
                  <div className='w-full mt-2'>
                    <p className='w-full text-[1rem] leading-relaxed'>
                      Available Dates
                    </p>
                    <table className='min-w-full bg-white text-sm'>
                      <thead className='sticky top-0 border-b bg-white ltr:text-left rtl:text-right'>
                        <tr>
                          <th className='whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900'>
                            Date
                          </th>
                          <th className='whitespace-nowrap px-4 py-2 text-left font-medium text-gray-900'>
                            Available
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-200'>
                        {availabilityData?.selectedDates.map((date, index) => {
                          const isoDate = new Date(date.date);
                          const newDate = `${
                            isoDate.getMonth() + 1
                          }-${isoDate.getDate()}-${isoDate.getFullYear()}`;
                          return (
                            <tr className='odd:bg-gray-50' key={index}>
                              <td className='whitespace-nowrap px-4 py-2 font-medium text-gray-900'>
                                {newDate || '-'}
                              </td>
                              <td className='whitespace-nowrap px-4 py-2 text-gray-700'>
                                {date.selected ? 'Yes' : 'No'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            )
          )}
          <Alert
            color={isError ? 'danger' : isSuccess ? 'success' : 'danger'}
            text={
              isError
                ? error
                  ? error
                  : 'Something went wrong'
                : isSuccess
                ? successMessage
                : null
            }
            showAlert={isError || isSuccess}
            onClose={onClose}
            isShowClose={isSuccess ? false : true}
          />
        </div>
        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='secondary' color='' onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>

      {/* Availability Modal */}
      <Modal
        open={isOpenEditUserModal}
        onClose={() => {
          setIsOpenEditUserModal(false);
          onClose();
        }}
        title='Edit User Info'
        icon={<EditUserIcon className='w-[20px] h-[20px]' />}
      >
        <div className='mt-4'>
          {restProps?.row?.profile === null ? (
            <Alert
              color={'success'}
              text={"This user didn't have profile"}
              showAlert={true}
              onClose={onClose}
              isShowClose={false}
            />
          ) : (
            <div className='flex flex-col gap-4  items-start mt-2'>
              {userData?.email && (
                <TextField
                  placeholder='Email'
                  name='email'
                  value={userData.email}
                  disabled
                  onChange={handleOnChange}
                />
              )}
              {userData?.phoneNumber && (
                <TextField
                  placeholder='Phone Number'
                  name='phoneNumber'
                  value={userData.phoneNumber}
                  disabled
                  onChange={handleOnChange}
                />
              )}
              <TextField
                placeholder='First Name'
                name='firstName'
                value={userData.firstName}
                onChange={handleOnChange}
              />
              <TextField
                placeholder='Last Name'
                name='lastName'
                value={userData.lastName}
                onChange={handleOnChange}
              />
              <div className='flex justify-between w-full gap-2'>
                <TextField
                  placeholder='State'
                  name='state'
                  value={userData.state}
                  onChange={handleOnChange}
                />
                <TextField
                  placeholder='Zipcode'
                  name='zipcode'
                  value={userData.zipcode}
                  onChange={handleOnChange}
                />
              </div>
              <TextField
                placeholder='Address'
                name='address'
                value={userData.address}
                onChange={handleOnChange}
              />
            </div>
          )}
          <Alert
            color={isError ? 'danger' : isSuccess ? 'success' : 'danger'}
            text={
              isError
                ? error
                  ? error
                  : 'Something went wrong'
                : isSuccess
                ? successMessage
                : null
            }
            showAlert={isError || isSuccess}
            onClose={onClose}
            isShowClose={isSuccess ? false : true}
          />
        </div>
        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='secondary' color='' onClick={onClose}>
            Close
          </Button>
          {restProps?.row?.profile !== null && (
            <Button
              variant='primary'
              color='primary'
              onClick={handleUpdateUser}
              loading={loading}
            >
              Update
            </Button>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};
